import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSearch,
  FiTrendingUp,
  FiZap,
  FiBookmark,
  FiFilter,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiCheck,
  FiSquare,
  FiAlertCircle,
  FiFileText,
  FiGlobe,
  FiHash,
  FiArrowRight,
} from "react-icons/fi";
import {
  searchPapers,
  getRecommendations,
  getTrendingPapers,
  bulkSavePapers,
  getSavedPapers,
  unsavePaper,
  exportSavedPapers,
  getSuggestions,
  getTrendingTopics,
} from "../../services/paperService";
import { useUserContext } from "../../context/UserContext";
import PaperCard from "./PaperCard";
import SavedPapers from "./SavedPapers";
import "./styles/ExplorePapers.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const ExplorePapers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useUserContext();

  // URL parameters
  const queryParams = new URLSearchParams(location.search);
  const queryFromUrl = queryParams.get("query");
  const fieldFromUrl = queryParams.get("field");
  const yearFromUrl = queryParams.get("year");
  const sortByFromUrl = queryParams.get("sortBy");
  const tabFromUrl = queryParams.get("tab");
  const viewFromUrl = queryParams.get("view");

  // Determine initial tab based on URL params
  const getInitialTab = () => {
    if (
      tabFromUrl &&
      ["search", "trending", "recommended"].includes(tabFromUrl)
    ) {
      return tabFromUrl;
    }
    if (queryFromUrl) {
      return "search";
    }
    return "recommended";
  };

  // State
  const [inputQuery, setInputQuery] = useState(queryFromUrl || "");
  const [currentQuery, setCurrentQuery] = useState(queryFromUrl || "");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [papers, setPapers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [error, setError] = useState("");
  const [mode, setMode] = useState(queryFromUrl ? "search" : "recommended");
  const [showingSavedPapers, setShowingSavedPapers] = useState(
    viewFromUrl === "saved"
  );

  // Saved papers state
  const [savedPapersData, setSavedPapersData] = useState([]);
  const [savedPapersLoading, setSavedPapersLoading] = useState(false);

  // Filter states
  const [selectedField, setSelectedField] = useState(fieldFromUrl || "");
  const [selectedYear, setSelectedYear] = useState(yearFromUrl || "");
  const [sortBy, setSortBy] = useState(sortByFromUrl || "relevance");

  // Advanced filter states
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [minCitations, setMinCitations] = useState("");
  const [maxCitations, setMaxCitations] = useState("");
  const [openAccessOnly, setOpenAccessOnly] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState("");
  const [selectedJournal, setSelectedJournal] = useState("");
  const [selectedPublisher, setSelectedPublisher] = useState("");

  // UI states
  const [facets, setFacets] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [trendingTimeframe, setTrendingTimeframe] = useState("week");
  const [selectedPapers, setSelectedPapers] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // NEW: Autocomplete & suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // NEW: Global paper count from OpenAlex
  const [openAlexTotal, setOpenAlexTotal] = useState(null);

  // NEW: Trending topics state
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [trendingTopicsLoading, setTrendingTopicsLoading] = useState(false);

  // Sort options
  const sortOptions = [
    { value: "relevance", label: "Relevance" },
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "citations", label: "Most Cited" },
  ];

  // Search function
  const performSearch = useCallback(
    async (searchQuery, page) => {
      setIsLoading(true);
      setError("");

      try {
        const searchParams = {
          query: searchQuery,
          page: page,
          limit: pagination.limit,
          ...(selectedField && { field: selectedField }),
          ...(selectedYear && { year: selectedYear }),
          sort: sortBy || "relevance",
          ...(yearFrom && { yearFrom: parseInt(yearFrom) }),
          ...(yearTo && { yearTo: parseInt(yearTo) }),
          ...(minCitations && { minCitations: parseInt(minCitations) }),
          ...(maxCitations && { maxCitations: parseInt(maxCitations) }),
          ...(openAccessOnly && { openAccessOnly: true }),
          ...(selectedTopics && { topics: selectedTopics }),
          ...(selectedJournal && { journal: selectedJournal }),
          ...(selectedPublisher && { publisher: selectedPublisher }),
        };

        const response = await searchPapers(searchParams);

        if (response && response.data && Array.isArray(response.data)) {
          setPapers(response.data);
          setPagination({
            ...pagination,
            page: page,
            total: response.pagination?.total || response.data.length,
            totalPages:
              response.pagination?.totalPages ||
              Math.ceil(response.data.length / pagination.limit),
          });

          // NEW: Capture global paper count from OpenAlex
          if (response.pagination?.openAlexTotal) {
            setOpenAlexTotal(response.pagination.openAlexTotal);
          } else {
            setOpenAlexTotal(null);
          }

          if (response.facets) {
            setFacets(response.facets);
          }
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        setError(
          "Failed to search papers. Please check your connection and try again."
        );
        setPapers([]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      pagination.limit,
      selectedField,
      selectedYear,
      sortBy,
      yearFrom,
      yearTo,
      minCitations,
      maxCitations,
      openAccessOnly,
      selectedTopics,
      selectedJournal,
      selectedPublisher,
    ]
  );

  // Load recommended papers
  const loadRecommendedPapers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await getRecommendations(20);

      if (response && response.data && Array.isArray(response.data)) {
        setPapers(response.data);
        setPagination({
          ...pagination,
          total: response.pagination?.total || response.data.length,
          totalPages:
            response.pagination?.totalPages ||
            Math.ceil(response.data.length / pagination.limit),
        });
        setMode("recommended");
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      setError(
        "Could not load recommended papers. Please try searching instead."
      );
      setPapers([]);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.limit]);

  // Load trending papers
  const loadTrendingPapers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await getTrendingPapers({
        timeframe: trendingTimeframe,
        limit: 20,
      });

      if (response && response.data && Array.isArray(response.data)) {
        setPapers(response.data);
        setPagination({
          ...pagination,
          total: response.pagination?.total || response.data.length,
          totalPages:
            response.pagination?.totalPages ||
            Math.ceil(response.data.length / pagination.limit),
        });
        setMode("trending");
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      setError("Could not load trending papers. Please try again.");
      setPapers([]);
    } finally {
      setIsLoading(false);
    }
  }, [trendingTimeframe, pagination.limit]);

  // NEW: Debounce reference for suggestions
  const suggestionsTimeoutRef = useRef(null);

  // NEW: Fetch autocomplete suggestions with debounce
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Clear previous timeout
    if (suggestionsTimeoutRef.current) {
      clearTimeout(suggestionsTimeoutRef.current);
    }

    // Debounce 300ms
    suggestionsTimeoutRef.current = setTimeout(async () => {
      try {
        setSuggestionsLoading(true);
        const response = await getSuggestions(query);

        if (response && response.suggestions) {
          setSuggestions(response.suggestions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
        }
      } catch (err) {
        // Silently handle suggestion fetch errors
      } finally {
        setSuggestionsLoading(false);
      }
    }, 300);
  }, []);

  // NEW: Load trending topics
  const loadTrendingTopicsData = useCallback(async () => {
    try {
      setTrendingTopicsLoading(true);
      const response = await getTrendingTopics();

      if (response && response.topics) {
        setTrendingTopics(response.topics);
      }
    } catch (err) {
      // Silently handle trending topics fetch errors
    } finally {
      setTrendingTopicsLoading(false);
    }
  }, []);

  // NEW: Load trending topics on mount
  useEffect(() => {
    loadTrendingTopicsData();
  }, [loadTrendingTopicsData]);

  // NEW: Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    // For paper suggestions, use DOI for navigation
    if (suggestion.type === "paper" && suggestion.doi) {
      navigate(`/explore/${encodeURIComponent(suggestion.doi)}`);
    } else {
      // For topic/keyword suggestions or papers without DOI, trigger a search
      setInputQuery(suggestion.text);
      setShowSuggestions(false);
      const params = new URLSearchParams();
      params.append("query", suggestion.text);
      navigate(`/explore?${params.toString()}`, { replace: true });
      setCurrentQuery(suggestion.text);
      setMode("search");
      performSearch(suggestion.text, 1);
    }
  };

  // NEW: Close suggestions when clicking outside
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Load papers based on active tab
  useEffect(() => {
    const loadPapers = async () => {
      if (showingSavedPapers) return;

      // For search tab without a query, just show empty state - don't load recommendations
      if (activeTab === "search" && !queryFromUrl) {
        setPapers([]);
        setIsInitialLoading(false);
        return;
      }

      setIsInitialLoading(true);
      try {
        if (activeTab === "search" && queryFromUrl) {
          await performSearch(queryFromUrl, 1);
        } else if (activeTab === "trending") {
          await loadTrendingPapers();
        } else if (activeTab === "recommended") {
          await loadRecommendedPapers();
        }
      } catch (err) {
        setError("Failed to load papers. Please try refreshing the page.");
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadPapers();
  }, [activeTab, trendingTimeframe, showingSavedPapers]);

  // Load saved papers when showing saved papers view
  const loadSavedPapers = useCallback(async () => {
    const userId = user?._id || user?.id;
    if (!userId) {
      return;
    }

    try {
      setSavedPapersLoading(true);
      const response = await getSavedPapers(userId, 1, 100);

      if (response && response.data && Array.isArray(response.data)) {
        // Transform the data: the backend returns { _id, user, paper: {...}, savedAt }
        // We need to flatten it so the paper data is at the top level
        const transformedPapers = response.data.map((item) => ({
          // Use the saved paper record's _id as the unique identifier
          id: item._id,
          savedAt: item.savedAt || item.createdAt,
          // Spread the nested paper object to flatten the structure
          ...item.paper,
          // Ensure we have an id field (use paper's _id or paperId if available)
          paperId: item.paper?._id || item.paper?.paperId || item.paper?.id,
        }));
        setSavedPapersData(transformedPapers);
      } else if (response && Array.isArray(response)) {
        // If response is directly an array, transform it similarly
        const transformedPapers = response.map((item) => ({
          id: item._id,
          savedAt: item.savedAt || item.createdAt,
          ...item.paper,
          paperId: item.paper?._id || item.paper?.paperId || item.paper?.id,
        }));
        setSavedPapersData(transformedPapers);
      } else {
        setSavedPapersData([]);
      }
    } catch (err) {
      setSavedPapersData([]);
    } finally {
      setSavedPapersLoading(false);
    }
  }, [user?._id, user?.id]);

  // Fetch saved papers when toggling to saved papers view
  useEffect(() => {
    if (showingSavedPapers && isAuthenticated) {
      loadSavedPapers();
    }
  }, [showingSavedPapers, isAuthenticated, loadSavedPapers]);

  // Handle removing a saved paper
  const handleRemoveSavedPaper = async (paperId) => {
    try {
      await unsavePaper(paperId);
      setSavedPapersData((prev) =>
        prev.filter((p) => p._id !== paperId && p.paperId !== paperId)
      );
    } catch (err) {
      // Silently handle remove paper errors
    }
  };

  // Handle exporting saved papers
  const handleExportSavedPapers = async (papers, format) => {
    try {
      const userId = user?._id || user?.id;
      if (!userId) return;
      await exportSavedPapers(userId, format);
    } catch (err) {
      // Silently handle export errors
    }
  };

  // Re-run search when filters change
  useEffect(() => {
    if (mode === "search" && currentQuery && !isInitialLoading) {
      performSearch(currentQuery, 1);
    }
  }, [
    yearFrom,
    yearTo,
    minCitations,
    maxCitations,
    openAccessOnly,
    selectedTopics,
    selectedJournal,
    selectedPublisher,
    sortBy,
  ]);

  // Input sanitization
  const sanitizeInput = (input) => {
    if (!input) return "";
    let sanitized = input.trim();
    sanitized = sanitized.replace(/<[^>]*>?/gm, "");
    sanitized = sanitized.replace(/javascript:/gi, "");
    sanitized = sanitized.replace(/on\w+=/gi, "");
    return sanitized.substring(0, 500);
  };

  // Event handlers
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputQuery(value);
    // NEW: Fetch suggestions as user types
    fetchSuggestions(value);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!inputQuery.trim() && !selectedField && !selectedYear) return;

    const params = new URLSearchParams();
    if (inputQuery.trim()) {
      const sanitizedQuery = sanitizeInput(inputQuery);
      params.append("query", sanitizedQuery);
    }
    if (selectedField) params.append("field", selectedField);
    if (selectedYear) params.append("year", selectedYear);
    if (sortBy) params.append("sortBy", sortBy);

    navigate(`/explore?${params.toString()}`, { replace: true });
    setCurrentQuery(inputQuery);
    setMode("search");
    performSearch(inputQuery, 1);
  };

  const handlePageChange = async (newPage) => {
    if (mode === "search") {
      await performSearch(currentQuery, newPage);
    } else {
      await loadRecommendedPapers();
    }
  };

  const clearFilters = () => {
    setSelectedField("");
    setSelectedYear("");
    setSortBy("relevance");
    setYearFrom("");
    setYearTo("");
    setMinCitations("");
    setMaxCitations("");
    setOpenAccessOnly(false);
    setSelectedTopics("");
    setSelectedJournal("");
    setSelectedPublisher("");

    if (currentQuery) {
      performSearch(currentQuery, 1);
    } else {
      loadRecommendedPapers();
    }

    navigate("/explore", { replace: true });
  };

  const togglePaperSelection = (paperId) => {
    setSelectedPapers((prev) =>
      prev.includes(paperId)
        ? prev.filter((id) => id !== paperId)
        : [...prev, paperId]
    );
  };

  const selectAllPapers = () => {
    if (selectedPapers.length === papers.length) {
      setSelectedPapers([]);
    } else {
      setSelectedPapers(papers.map((paper) => paper._id || paper.doi));
    }
  };

  const handleBulkSave = async () => {
    if (selectedPapers.length === 0) return;

    try {
      setBulkActionLoading(true);
      const result = await bulkSavePapers(selectedPapers);

      if (result.success) {
        alert(
          `Saved ${result.data.saved.length} papers. ${result.data.alreadySaved.length} were already saved.`
        );
        setSelectedPapers([]);
      }
    } catch (err) {
      setError("Failed to save selected papers. Please try again.");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMode(tab);
    setSelectedPapers([]);
    setError("");

    // Update URL with tab parameter to preserve state on navigation
    const params = new URLSearchParams(location.search);
    params.set("tab", tab);
    if (tab !== "search") {
      params.delete("query");
    }
    navigate(`/explore?${params.toString()}`, { replace: true });
  };

  const hasActiveFilters =
    selectedField ||
    selectedYear ||
    sortBy !== "relevance" ||
    yearFrom ||
    yearTo ||
    minCitations ||
    maxCitations ||
    openAccessOnly ||
    selectedTopics ||
    selectedJournal ||
    selectedPublisher;

  // Render papers list
  const renderPapersList = () => {
    if (isInitialLoading) {
      return (
        <motion.div
          className="exp-loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="exp-loading-spinner" />
          <p className="exp-loading-text">Loading papers...</p>
        </motion.div>
      );
    }

    if (error) {
      return (
        <motion.div
          className="exp-error"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="exp-error-icon">
            <FiAlertCircle />
          </div>
          <p className="exp-error-text">{error}</p>
        </motion.div>
      );
    }

    if (papers.length === 0) {
      // Determine the appropriate empty state based on active tab
      const getEmptyState = () => {
        if (activeTab === "search") {
          return {
            icon: <FiSearch />,
            title: "Discover Research Papers",
            text: "Search for topics you're passionate about to explore millions of academic papers and research articles.",
          };
        } else if (activeTab === "trending") {
          return {
            icon: <FiTrendingUp />,
            title: "No Trending Papers",
            text: "There are no trending papers available at the moment. Check back later for updates.",
          };
        } else {
          return {
            icon: <FiFileText />,
            title: "No Recommendations Yet",
            text: "Start by searching for papers in your area of interest to get personalized recommendations.",
          };
        }
      };

      const emptyState = getEmptyState();

      return (
        <motion.div
          className="exp-empty"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="exp-empty-icon">{emptyState.icon}</div>
          <h3 className="exp-empty-title">{emptyState.title}</h3>
          <p className="exp-empty-text">{emptyState.text}</p>
        </motion.div>
      );
    }

    return (
      <>
        {/* Bulk Actions Toolbar */}
        {papers.length > 0 && isAuthenticated && (
          <motion.div
            className="exp-bulk-toolbar"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button onClick={selectAllPapers} className="exp-select-all-btn">
              {selectedPapers.length === papers.length ? (
                <>
                  <FiCheck /> Deselect All
                </>
              ) : (
                <>
                  <FiSquare /> Select All
                </>
              )}
            </button>
            {selectedPapers.length > 0 && (
              <motion.button
                onClick={handleBulkSave}
                disabled={bulkActionLoading}
                className="exp-bulk-save-btn"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <FiBookmark />
                {bulkActionLoading
                  ? "Saving..."
                  : `Save Selected (${selectedPapers.length})`}
              </motion.button>
            )}
          </motion.div>
        )}

        {/* Papers Grid */}
        <motion.div
          className="exp-papers-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <AnimatePresence>
            {papers.map((paper, index) => {
              const paperId = paper._id || paper.doi;
              const isSelected = selectedPapers.includes(paperId);

              return (
                <motion.div
                  key={paperId}
                  className={`exp-paper-wrapper ${
                    isSelected ? "selected" : ""
                  }`}
                  variants={itemVariants}
                  layout
                >
                  {isAuthenticated && (
                    <div className="exp-paper-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => togglePaperSelection(paperId)}
                        aria-label={`Select ${paper.title}`}
                      />
                    </div>
                  )}
                  <PaperCard paper={paper} />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {/* Pagination */}
        <motion.div
          className="exp-pagination"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <button
            className="exp-page-btn"
            disabled={pagination.page === 1 || isLoading}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            <FiChevronLeft /> Previous
          </button>
          <div className="exp-page-info">
            Page <span>{pagination.page}</span> of{" "}
            <span>{pagination.totalPages || 1}</span>
          </div>
          <button
            className="exp-page-btn"
            disabled={
              pagination.page === pagination.totalPages ||
              pagination.totalPages === 0 ||
              isLoading
            }
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next <FiChevronRight />
          </button>
        </motion.div>
      </>
    );
  };

  return (
    <div className="exp-container">
      <div className="exp-wrapper">
        <AnimatePresence mode="wait">
          {showingSavedPapers ? (
            <motion.div
              key="saved-papers"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="exp-back-header">
                <button
                  className="exp-back-btn"
                  onClick={() => {
                    setShowingSavedPapers(false);
                    navigate("/explore", { replace: true });
                  }}
                >
                  <FiChevronLeft /> Back to Explore
                </button>
              </div>
              <SavedPapers
                savedPapers={savedPapersData}
                loading={savedPapersLoading}
                onRemove={handleRemoveSavedPaper}
                onExport={handleExportSavedPapers}
              />
            </motion.div>
          ) : (
            <motion.div
              key="explore-papers"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Header */}
              <motion.div
                className="exp-header"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="exp-header-top">
                  <div className="exp-header-title-section">
                    <div className="exp-header-title-row">
                      <h1 className="exp-title">Explore Research Papers</h1>
                    </div>
                    <p className="exp-subtitle">
                      Search millions of research papers across various fields
                    </p>
                  </div>
                  {isAuthenticated && (
                    <motion.button
                      className="exp-saved-btn"
                      onClick={() => {
                        setShowingSavedPapers(true);
                        navigate("/explore?view=saved", { replace: true });
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FiBookmark /> Saved Papers
                    </motion.button>
                  )}
                </div>

                {/* Tabs */}
                <div className="exp-tabs">
                  <button
                    className={`exp-tab-btn exp-tab-btn--search ${
                      activeTab === "search" ? "active" : ""
                    }`}
                    onClick={() => handleTabChange("search")}
                  >
                    <FiSearch />
                    <span className="exp-tab-text">Search</span>
                  </button>
                  <button
                    className={`exp-tab-btn exp-tab-btn--trending ${
                      activeTab === "trending" ? "active" : ""
                    }`}
                    onClick={() => handleTabChange("trending")}
                  >
                    <FiTrendingUp />
                    <span className="exp-tab-text">Trending</span>
                  </button>
                  <button
                    className={`exp-tab-btn exp-tab-btn--recommended ${
                      activeTab === "recommended" ? "active" : ""
                    }`}
                    onClick={() => handleTabChange("recommended")}
                  >
                    <FiZap />
                    <span className="exp-tab-text">For You</span>
                  </button>
                </div>
              </motion.div>

              {/* Trending Controls */}
              <AnimatePresence>
                {activeTab === "trending" && (
                  <motion.div
                    className="exp-trending-section"
                    variants={fadeInVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <div className="exp-trending-controls">
                      <FiTrendingUp className="exp-trending-icon" />
                      <span className="exp-trending-text">Trending</span>
                      <select
                        value={trendingTimeframe}
                        onChange={(e) => setTrendingTimeframe(e.target.value)}
                        className="exp-timeframe-select"
                      >
                        <option value="week">this week</option>
                        <option value="month">this month</option>
                        <option value="all">all time</option>
                      </select>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Search Section */}
              <AnimatePresence>
                {activeTab === "search" && (
                  <motion.div
                    className="exp-search-section"
                    variants={fadeInVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <form className="exp-search-form" onSubmit={handleSearch}>
                      <div
                        className="exp-search-input-wrapper"
                        ref={searchInputRef}
                      >
                        <input
                          type="text"
                          className="exp-search-input"
                          placeholder="Search by title, author, keyword..."
                          value={inputQuery}
                          onChange={handleInputChange}
                          onFocus={() =>
                            inputQuery.length >= 2 && setShowSuggestions(true)
                          }
                          aria-label="Search papers"
                          autoComplete="off"
                        />
                        <button
                          type="submit"
                          className="exp-search-btn"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <div
                              className="exp-loading-spinner"
                              style={{ width: 18, height: 18, borderWidth: 2 }}
                            />
                          ) : (
                            <span className="exp-search-btn-text">Search</span>
                          )}
                        </button>

                        {/* NEW: Autocomplete Suggestions Dropdown */}
                        <AnimatePresence>
                          {showSuggestions && suggestions.length > 0 && (
                            <motion.div
                              className="exp-suggestions-dropdown"
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                            >
                              {suggestionsLoading && (
                                <div className="exp-suggestions-loading">
                                  <div
                                    className="exp-loading-spinner"
                                    style={{
                                      width: 16,
                                      height: 16,
                                      borderWidth: 2,
                                    }}
                                  />
                                  <span>Loading suggestions...</span>
                                </div>
                              )}
                              {suggestions.map((suggestion, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  className={`exp-suggestion-item exp-suggestion-item--${suggestion.type}`}
                                  onClick={() =>
                                    handleSuggestionClick(suggestion)
                                  }
                                >
                                  <div className="exp-suggestion-icon">
                                    {suggestion.type === "paper" ? (
                                      <FiFileText />
                                    ) : (
                                      <FiHash />
                                    )}
                                  </div>
                                  <div className="exp-suggestion-content">
                                    <span className="exp-suggestion-text">
                                      {suggestion.text}
                                    </span>
                                    {suggestion.type === "paper" &&
                                      suggestion.citations > 0 && (
                                        <span className="exp-suggestion-meta">
                                          {suggestion.citations.toLocaleString()}{" "}
                                          citations
                                        </span>
                                      )}
                                    {suggestion.type === "topic" && (
                                      <span className="exp-suggestion-type">
                                        Topic
                                      </span>
                                    )}
                                  </div>
                                  <FiArrowRight className="exp-suggestion-arrow" />
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Filter Controls */}
                      <div className="exp-controls-row">
                        <div className="exp-quick-filters">
                          <select
                            className="exp-select"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            aria-label="Sort results"
                          >
                            {sortOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>

                          <button
                            type="button"
                            className={`exp-filter-toggle-btn ${
                              showAdvancedFilters ? "active" : ""
                            }`}
                            onClick={() =>
                              setShowAdvancedFilters(!showAdvancedFilters)
                            }
                          >
                            <FiFilter />
                            {showAdvancedFilters
                              ? "Hide Filters"
                              : "More Filters"}
                          </button>

                          {hasActiveFilters && (
                            <button
                              type="button"
                              className="exp-clear-btn"
                              onClick={clearFilters}
                              aria-label="Clear filters"
                            >
                              <FiX /> Clear
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Advanced Filters */}
                      <AnimatePresence>
                        {showAdvancedFilters && (
                          <motion.div
                            className="exp-advanced-filters"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                          >
                            <div className="exp-filters-grid">
                              <div className="exp-filter-group">
                                <label className="exp-filter-label">
                                  Year Range
                                </label>
                                <div className="exp-range-inputs">
                                  <input
                                    type="number"
                                    className="exp-filter-input"
                                    placeholder="From"
                                    value={yearFrom}
                                    onChange={(e) =>
                                      setYearFrom(e.target.value)
                                    }
                                    min="1900"
                                    max="2100"
                                  />
                                  <span>to</span>
                                  <input
                                    type="number"
                                    className="exp-filter-input"
                                    placeholder="To"
                                    value={yearTo}
                                    onChange={(e) => setYearTo(e.target.value)}
                                    min="1900"
                                    max="2100"
                                  />
                                </div>
                              </div>

                              <div className="exp-filter-group">
                                <label className="exp-filter-label">
                                  Citations
                                </label>
                                <div className="exp-range-inputs">
                                  <input
                                    type="number"
                                    className="exp-filter-input"
                                    placeholder="Min"
                                    value={minCitations}
                                    onChange={(e) =>
                                      setMinCitations(e.target.value)
                                    }
                                    min="0"
                                  />
                                  <span>to</span>
                                  <input
                                    type="number"
                                    className="exp-filter-input"
                                    placeholder="Max"
                                    value={maxCitations}
                                    onChange={(e) =>
                                      setMaxCitations(e.target.value)
                                    }
                                    min="0"
                                  />
                                </div>
                              </div>

                              <div className="exp-filter-group">
                                <label className="exp-filter-label">
                                  Access Type
                                </label>
                                <label className="exp-checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={openAccessOnly}
                                    onChange={(e) =>
                                      setOpenAccessOnly(e.target.checked)
                                    }
                                  />
                                  Open Access Only
                                </label>
                              </div>

                              <div className="exp-filter-group">
                                <label className="exp-filter-label">
                                  Topics
                                </label>
                                <input
                                  type="text"
                                  className="exp-filter-input"
                                  placeholder="e.g., Machine Learning, AI"
                                  value={selectedTopics}
                                  onChange={(e) =>
                                    setSelectedTopics(e.target.value)
                                  }
                                />
                              </div>

                              <div className="exp-filter-group">
                                <label className="exp-filter-label">
                                  Journal
                                </label>
                                <input
                                  type="text"
                                  className="exp-filter-input"
                                  placeholder="Journal name"
                                  value={selectedJournal}
                                  onChange={(e) =>
                                    setSelectedJournal(e.target.value)
                                  }
                                />
                              </div>

                              <div className="exp-filter-group">
                                <label className="exp-filter-label">
                                  Publisher
                                </label>
                                <input
                                  type="text"
                                  className="exp-filter-input"
                                  placeholder="Publisher name"
                                  value={selectedPublisher}
                                  onChange={(e) =>
                                    setSelectedPublisher(e.target.value)
                                  }
                                />
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Facets Panel */}
              <AnimatePresence>
                {facets && activeTab === "search" && (
                  <motion.div
                    className="exp-facets-panel"
                    variants={fadeInVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <h4 className="exp-facets-title">Refine Results</h4>

                    {facets.years && facets.years.length > 0 && (
                      <div className="exp-facet-group">
                        <h5 className="exp-facet-group-title">
                          Publication Year
                        </h5>
                        <div className="exp-facet-items">
                          {facets.years.slice(0, 5).map((year) => (
                            <button
                              key={year.key}
                              className={`exp-facet-btn ${
                                yearFrom === year.key.toString() ? "active" : ""
                              }`}
                              onClick={() => {
                                const yearStr = year.key.toString();
                                setYearFrom(yearStr);
                                setYearTo(yearStr);
                              }}
                            >
                              {year.key}
                              <span className="exp-facet-count">
                                ({year.doc_count})
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {facets.topics && facets.topics.length > 0 && (
                      <div className="exp-facet-group">
                        <h5 className="exp-facet-group-title">Topics</h5>
                        <div className="exp-facet-items">
                          {facets.topics.slice(0, 5).map((topic) => (
                            <button
                              key={topic.key}
                              className={`exp-facet-btn ${
                                selectedTopics === topic.key ? "active" : ""
                              }`}
                              onClick={() => setSelectedTopics(topic.key)}
                            >
                              {topic.key}
                              <span className="exp-facet-count">
                                ({topic.doc_count})
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {facets.openAccess && facets.openAccess.length > 0 && (
                      <div className="exp-facet-group">
                        <h5 className="exp-facet-group-title">Access Type</h5>
                        <div className="exp-facet-items">
                          {facets.openAccess.map((access) => (
                            <button
                              key={access.key.toString()}
                              className={`exp-facet-btn ${
                                openAccessOnly === access.key ? "active" : ""
                              }`}
                              onClick={() => setOpenAccessOnly(access.key)}
                            >
                              {access.key ? "Open Access" : "Restricted"}
                              <span className="exp-facet-count">
                                ({access.doc_count})
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Results Info */}
              <AnimatePresence>
                {activeTab === "search" &&
                  mode === "search" &&
                  papers.length > 0 &&
                  !isLoading && (
                    <motion.div
                      className="exp-results-info-container"
                      variants={fadeInVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <div className="exp-results-info exp-results-info--search">
                        <FiFileText />
                        Found{" "}
                        <span className="exp-results-count">
                          {pagination.total.toLocaleString()}
                        </span>{" "}
                        results for "
                        <span className="exp-results-query">
                          {currentQuery}
                        </span>
                        "
                      </div>

                      {/* NEW: Global Paper Count from OpenAlex */}
                      {openAlexTotal && openAlexTotal > pagination.total && (
                        <div className="exp-global-count">
                          <FiGlobe className="exp-global-count-icon" />
                          <span className="exp-global-count-text">
                            <span className="exp-global-count-number">
                              {openAlexTotal.toLocaleString()}
                            </span>{" "}
                            papers available globally
                          </span>
                        </div>
                      )}
                    </motion.div>
                  )}

                {activeTab === "recommended" &&
                  papers.length > 0 &&
                  !isLoading && (
                    <motion.div
                      className="exp-results-info exp-results-info--recommended"
                      variants={fadeInVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <FiZap />
                      Personalized recommendations based on your interests
                    </motion.div>
                  )}
              </AnimatePresence>

              {/* NEW: Trending Topics Section - Shows on Search (no query) and Trending tabs */}
              <AnimatePresence>
                {((activeTab === "search" && !currentQuery) ||
                  activeTab === "trending") &&
                  trendingTopics.length > 0 && (
                    <motion.div
                      className="exp-trending-topics-section"
                      variants={fadeInVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      <div className="exp-trending-topics-header">
                        <FiTrendingUp className="exp-trending-topics-icon" />
                        <h3 className="exp-trending-topics-title">
                          {activeTab === "trending"
                            ? "Hot Topics Right Now"
                            : "Trending Topics"}
                        </h3>
                      </div>
                      <div className="exp-trending-topics-grid">
                        {trendingTopicsLoading ? (
                          <div className="exp-trending-topics-loading">
                            <div
                              className="exp-loading-spinner"
                              style={{ width: 24, height: 24, borderWidth: 2 }}
                            />
                          </div>
                        ) : (
                          trendingTopics
                            .slice(0, activeTab === "trending" ? 12 : 8)
                            .map((topic, index) => (
                              <motion.button
                                key={index}
                                className="exp-trending-topic-card"
                                onClick={() => {
                                  setInputQuery(topic.topic);
                                  setCurrentQuery(topic.topic);
                                  setMode("search");
                                  handleTabChange("search");
                                  performSearch(topic.topic, 1);
                                  const params = new URLSearchParams();
                                  params.append("query", topic.topic);
                                  navigate(`/explore?${params.toString()}`, {
                                    replace: true,
                                  });
                                }}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                              >
                                <span className="exp-trending-topic-name">
                                  {topic.isHot && <span className="exp-hot-badge">🔥</span>}
                                  {topic.topic}
                                </span>
                                <div className="exp-trending-topic-stats">
                                  <span className="exp-trending-topic-count">
                                    <FiFileText />
                                    {topic.paperCount?.toLocaleString() || 0}
                                  </span>
                                  <span className="exp-trending-topic-citations">
                                    <FiHash />
                                    {Math.round(
                                      topic.avgCitations || 0
                                    ).toLocaleString()}{" "}
                                    avg
                                  </span>
                                </div>
                              </motion.button>
                            ))
                        )}
                      </div>
                    </motion.div>
                  )}
              </AnimatePresence>

              {/* Papers List */}
              {renderPapersList()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ExplorePapers;
